import React, { useEffect, useState } from "react";
import axios from "axios";
import { displayCurrency } from "../helpers/functions";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Card,
  Typography,
  Space,
  Spin,
  Divider,
  Button,
  Drawer,
  InputNumber,
  Select,
  message,
  Alert,
} from "antd";
import TonBalance from "./TonBalance";
import {
  useTonWallet,
  useTonConnectUI,
  useTonAddress,
} from "@tonconnect/ui-react";
import { Cell } from "@ton/ton";
import { useNavigate } from "react-router-dom";
import { fetchTonData } from "../services/slices/tonSlice";
import { fetchUSDTData } from "../services/slices/usdtSlice";
import { RootState, AppDispatch } from "../services/store";
import {
  WalletOutlined,
  GiftOutlined,
  TeamOutlined,
  LockOutlined,
  RightOutlined,
  RocketOutlined,
  BankOutlined,
  DollarCircleOutlined,
  PlusCircleOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { fetchWebPrice } from "../services/slices/webPriceSlice";
import {
  fetchOvernightInternalBalances,
  getPaymentWallet,
  VoucherStatus,
} from "../helpers/api";
import { sendTransactionOne } from "../helpers/transactionService";
import VoucherBalanceCard from "./VoucherBalanceCard";

const UNFREEZE_API =
  typeof window !== "undefined" &&
  window.location.hostname.startsWith("developer-")
    ? `${window.location.origin}/api/unfreeze`
    : "/api/unfreeze";

const { Text, Title } = Typography;

interface BalancesProps {
  externalVoucherTrigger?: boolean;
  onVoucherDrawerClose?: () => void;
  voucher?: VoucherStatus | null;
}

const Balances: React.FC<BalancesProps> = ({
  externalVoucherTrigger,
  onVoucherDrawerClose,
  voucher = null,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const wallet = useTonWallet();
  const userFriendlyAddress = useTonAddress();
  const [tonConnectUI] = useTonConnectUI();
  const navigate = useNavigate();
  const { userData, isLoading } = useSelector((state: RootState) => state.user);
  const { currentPrice, isLoading: priceLoading } = useSelector(
    (state: RootState) => state.webPrice
  );
  const usdtJettonWallet = useSelector(
    (state: RootState) => state.usdt.wallet_address
  );
  const [unfreezeEligible, setUnfreezeEligible] = useState(false);
  const [internalBalances, setInternalBalances] = useState<{ usdt: number; ton: number } | null>(null);
  const [isIssueLoanDrawerOpen, setIsIssueLoanDrawerOpen] = useState(false);

  const [isDepositDrawerOpen, setIsDepositDrawerOpen] = useState(false);
  const [isVoucherDepositMode, setIsVoucherDepositMode] = useState(false);
  const [depositCurrency, setDepositCurrency] = useState<"TON" | "USDT">("USDT");
  const [depositAmount, setDepositAmount] = useState<number | null>(null);
  // depositPending — после успешной подписи в кошельке и до того как
  // working-баланс приедет с сервера; скрывает кнопку Пополнить и показывает баннер,
  // чтобы юзер не нажал «Пополнить» снова, увидев старый баланс.
  const [depositPending, setDepositPending] = useState<{
    amount: number;
    currency: "TON" | "USDT";
  } | null>(null);
  const [depositSending, setDepositSending] = useState(false);

  // Баннер ваучера на Home поднимает externalVoucherTrigger — открываем drawer
  // в режиме ваучера. Без этого кнопка на баннере не делает ничего.
  useEffect(() => {
    if (externalVoucherTrigger) {
      setIsVoucherDepositMode(true);
      setDepositCurrency("USDT");
      setDepositAmount(100);
      setIsDepositDrawerOpen(true);
    }
  }, [externalVoucherTrigger]);

  useEffect(() => {
    dispatch(fetchTonData(null));
    dispatch(fetchWebPrice());
  }, [dispatch]);

  useEffect(() => {
    if (userFriendlyAddress) {
      dispatch(fetchUSDTData(userFriendlyAddress));
    }
  }, [dispatch, userFriendlyAddress]);

  useEffect(() => {
    const loadInternal = async () => {
      try {
        const res = await fetchOvernightInternalBalances();
        if (res.success) {
          setInternalBalances({
            usdt: res.balance_overnight_usdt,
            ton: res.balance_overnight_ton
          });
        }
      } catch (e) {}
    };
    loadInternal();
    // Re-load working balance after a post-freeze withdraw completes.
    const onPfDone = () => loadInternal();
    window.addEventListener("pf-withdraw-success", onPfDone);
    return () => window.removeEventListener("pf-withdraw-success", onPfDone);
  }, []);

  useEffect(() => {
    if (!userData?.id) return;
    let cancelled = false;
    axios
      .get(`${UNFREEZE_API}/get-status.php`)
      .then((res) => {
        if (cancelled) return;
        const d = res.data || {};
        const hasFrozen =
          Number(d.frozen_own_ton || 0) > 0 ||
          Number(d.frozen_own_usdt || 0) > 0;
        setUnfreezeEligible(!!d.eligible && hasFrozen && !d.dev_bypass);
      })
      .catch(() => {
        if (!cancelled) setUnfreezeEligible(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  const calculateWebValue = () => {
    if (!userData?.web_tokens || !currentPrice) return 0;
    return (userData.web_tokens * currentPrice).toFixed(2);
  };

  const minDeposit = depositCurrency === "USDT" ? 10 : 1;

  // Итог по ваучеру считаем по тем же правилам, что и сервер: бонус равен
  // депозиту, но не больше max_bonus для валюты. max_bonus <= 0 означает
  // «без ограничения». Иначе UI обещал бы не то, что начислит voucher_lib.php.
  // Пользователю показываем GRAM, внутри и в блокчейне остаётся TON.
  const curLabel = displayCurrency(depositCurrency);
  const voucherDeposit = depositAmount || minDeposit;
  const voucherMaxBonus =
    (depositCurrency === "USDT" ? voucher?.max_bonus?.USDT : voucher?.max_bonus?.TON) ?? 0;
  const voucherBonus =
    voucherMaxBonus > 0 ? Math.min(voucherDeposit, voucherMaxBonus) : voucherDeposit;

  const handleDeposit = async () => {
    if (!userData?.id) {
      message.error(t("Auth required") as string);
      return;
    }
    if (!wallet || !userFriendlyAddress) {
      message.error(t("Connect Wallet") as string);
      return;
    }
    if (!depositAmount || depositAmount < minDeposit) {
      message.error(`${t("Minimum")} ${minDeposit} ${displayCurrency(depositCurrency)}`);
      return;
    }
    if (depositCurrency === "USDT" && !usdtJettonWallet) {
      message.error(t("USDT wallet not ready, try again in a moment") as string);
      return;
    }

    setDepositSending(true);
    try {
      const comment = isVoucherDepositMode
        ? `OvernightAddFunds:true;Voucher:true;UserId:${userData.id}`
        : `OvernightAddFunds:true;UserId:${userData.id}`;

      const walletResponse = await getPaymentWallet(userFriendlyAddress);
      if (!walletResponse || !walletResponse.walletAddress) {
        message.error(t("Couldn't get a payment address") as string);
        setDepositSending(false);
        return;
      }

      const tx = await sendTransactionOne(
        comment,
        depositCurrency,
        usdtJettonWallet,
        walletResponse.walletAddress,
        depositAmount
      );
      if (!tx || !tx.messages.length) {
        message.error(t("Transaction could not be created.") as string);
        setDepositSending(false);
        return;
      }

      // Закрываем drawer ДО ухода в кошелёк
      setIsDepositDrawerOpen(false);
      setIsVoucherDepositMode(false);
      if (onVoucherDrawerClose) onVoucherDrawerClose();

      const result = await tonConnectUI.sendTransaction(tx);
      const hash = Cell.fromBase64(result.boc).hash().toString("hex");
      console.log("[deposit] tx hash", hash);
      setDepositAmount(null);

      // Сразу показываем баннер «в обработке» и прячем кнопку Пополнить.
      // Telegram-диалог «Перейти по ссылке» иногда зависает поверх UI и юзер
      // переоткрывает экран; с баннером он сразу видит статус.
      const cur = depositCurrency;
      const amt = Number(depositAmount);
      setDepositPending({ amount: amt, currency: cur });

      const startWorking =
        cur === "USDT"
          ? Number(internalBalances?.usdt ?? userData?.balance_overnight_usdt ?? 0)
          : Number(internalBalances?.ton ?? userData?.balance_overnight_ton ?? 0);
      let attempts = 0;
      const refreshAll = async (): Promise<boolean> => {
        attempts++;
        if (userFriendlyAddress) {
          dispatch(fetchTonData(userFriendlyAddress));
          dispatch(fetchUSDTData(userFriendlyAddress));
        }
        try {
          const res = await fetchOvernightInternalBalances();
          if (res?.success) {
            setInternalBalances({
              usdt: res.balance_overnight_usdt,
              ton: res.balance_overnight_ton,
            });
            const curBal =
              cur === "USDT" ? res.balance_overnight_usdt : res.balance_overnight_ton;
            return curBal >= startWorking + amt - 0.0001;
          }
        } catch (_) {}
        return false;
      };
      const poll = async () => {
        const done = await refreshAll();
        if (done) {
          setDepositPending(null);
          message.success(t("The transaction has been sent") as string);
        } else if (attempts < 12) {
          setTimeout(poll, 5000);
        } else {
          // Тайм-аут поллинга: убираем баннер, но сообщаем юзеру что баланс
          // может прийти чуть позже (бэкенд-крон).
          setDepositPending(null);
          message.info(
            t("Payment is being processed. Balance will update shortly.") as string
          );
        }
      };
      setTimeout(poll, 4000);
    } catch (e) {
      console.error(e);
      // Молча игнорируем отмену пользователем; для других ошибок показываем тост.
      const msg = (e as any)?.message || "";
      if (!/reject|cancel|user/i.test(msg)) {
        message.error(t("Error when sending") as string);
      }
    } finally {
      setDepositSending(false);
    }
  };

  const incomeTotal = (Number(userData?.real_balance ?? 0) + Number(userData?.partner_balance ?? 0)).toFixed(2);

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      
      {/* YOUR BALANCE TITLE */}
      <div style={{ padding: '0 4px', marginBottom: '8px', textAlign: 'center' }}>
          <Title level={4} style={{ margin: 0, fontWeight: 700 }}>
            {t("yourBalance")}
          </Title>
      </div>

      {/* WALLET SECTION - NOW AT THE TOP */}
      <div style={{ padding: '0 4px', marginBottom: '8px' }}>
          <Divider orientation="left" style={{ margin: "0 0 12px 0", fontSize: '12px', color: '#8c8c8c' }}>
            {t("Connected Wallet")}
          </Divider>
          <Card
            style={{
              background: '#fff',
              borderRadius: '16px',
              border: '1px solid #f0f0f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <TonBalance />
          </Card>
      </div>

      {/* DOUBLE VOUCHER SECTION — секции нет, пока сервер не подтвердил ваучер */}
      {voucher && voucher.status !== "expired" && voucher.status !== "disabled" && (
        <div id="voucher-balance-section" style={{ padding: '0 4px', marginBottom: '8px' }}>
            <Divider orientation="left" style={{ margin: "0 0 12px 0", fontSize: '12px', color: '#8c8c8c' }}>
              🎫 {t("Personal Voucher")}
            </Divider>
            <VoucherBalanceCard
              voucher={voucher}
              onOpenVoucherDeposit={() => {
                setIsVoucherDepositMode(true);
                setDepositCurrency("USDT");
                setDepositAmount(voucher.min_deposit?.USDT ?? 10);
                setIsDepositDrawerOpen(true);
              }}
            />
        </div>
      )}

      {/* WORKING BALANCE SECTION (Always Open) */}
      <div style={{ padding: '0 4px', marginBottom: '8px' }}>
          <Divider orientation="left" style={{ margin: "0 0 12px 0", fontSize: '12px', color: '#8c8c8c' }}>
            {t("Work Balance")}
          </Divider>
          <Card 
            style={{ 
              background: '#fff', 
              borderRadius: '16px', 
              border: '1px solid #f0f0f0',
              boxShadow: '0 2px 8px rgba(0,0,0,0.02)' 
            }}
            bodyStyle={{ padding: '16px' }}
          >
            <Space direction="vertical" style={{ width: "100%" }} size={8}>
              <Card
                size="small"
                style={{ background: "#f8fafc", borderRadius: "8px" }}
              >
                <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                  <Space size={4}>
                    <img src="./assets/gramIcon.png" alt="GRAM" width={16} />
                    <Text style={{ fontSize: "13px" }}>GRAM</Text>
                  </Space>
                  <Text strong style={{ fontSize: "13px" }}>
                    {isLoading
                      ? <Spin size="small" />
                      : (Number(internalBalances?.ton) || Number(userData?.balance_overnight_ton) || 0).toFixed(4)}
                  </Text>
                </Space>
              </Card>

              <Card
                size="small"
                style={{ background: "#f8fafc", borderRadius: "12px" }}
              >
                <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
                  <Space>
                    <img src="./assets/UsdtLogo.webp" alt="USDT" width={16} />
                    <Text>USDT</Text>
                  </Space>
                  <Text strong style={{ fontSize: "13px" }}>
                    {isLoading
                      ? <Spin size="small" />
                      : (Number(internalBalances?.usdt) || Number(userData?.balance_overnight_usdt) || 0).toFixed(2)}
                  </Text>
                </Space>
              </Card>

              <Divider style={{ margin: "4px 0" }} />

              {depositPending && (
                <Alert
                  type="info"
                  showIcon
                  icon={<Spin size="small" />}
                  message={t("Payment sent — updating balance…") as string}
                  description={`${depositPending.amount} ${displayCurrency(depositPending.currency)}`}
                  style={{ borderRadius: "12px", marginBottom: 4 }}
                />
              )}

              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                {!depositPending && (
                  <Button
                    type="primary"
                    block
                    size="large"
                    icon={<PlusCircleOutlined />}
                    onClick={() => {
                      setIsVoucherDepositMode(false);
                      setIsDepositDrawerOpen(true);
                    }}
                    disabled={!wallet}
                    style={{ borderRadius: '12px', height: '48px', fontWeight: 600, background: '#52c41a', borderColor: '#52c41a' }}
                  >
                    {t("Top up")}
                  </Button>
                )}
                <Button
                    block
                    size="large"
                    icon={<BankOutlined />}
                    onClick={() => setIsIssueLoanDrawerOpen(true)}
                    style={{ borderRadius: '12px', height: '48px', fontWeight: 600 }}
                >
                    {t("Issue Loans")}
                </Button>
              </Space>
            </Space>
          </Card>
      </div>

      {/* MY INCOME SECTION */}
      <div style={{ padding: '0 4px', marginBottom: '8px' }}>
        <Divider orientation="left" style={{ margin: "0 0 12px 0", fontSize: '12px', color: '#8c8c8c' }}>
          <Space size={6}><DollarCircleOutlined style={{ color: "#52c41a" }} />{t("My Income")}</Space>
        </Divider>
        <Card
          style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #f0f0f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
          }}
          bodyStyle={{ padding: '16px' }}
        >
          <Space direction="vertical" style={{ width: "100%" }} size={12}>
            <div
              style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
              onClick={() => navigate("/withdraw")}
            >
              <Space size={8}>
                <WalletOutlined style={{ color: "#52c41a" }} />
                <Text>{t("Available for withdrawal")}</Text>
              </Space>
              <Text strong>{isLoading ? <Spin size="small" /> : `$${userData?.real_balance ?? 0}`}</Text>
            </div>
            <div
              style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
              onClick={() => navigate("/partners")}
            >
              <Space size={8}>
                <TeamOutlined style={{ color: "#722ed1" }} />
                <Text>{t("Partner bonuses")}</Text>
              </Space>
              <Text strong>{isLoading ? <Spin size="small" /> : `$${userData?.partner_balance ?? 0}`}</Text>
            </div>
            <Divider style={{ margin: "4px 0" }} />
            <Button
              block
              onClick={() => navigate("/withdraw")}
              style={{ borderRadius: '8px' }}
            >
              {t("Withdraw")}
            </Button>
          </Space>
        </Card>
      </div>

      {/* OTHER ASSETS SECTION */}
      <div style={{ padding: '0 4px', marginBottom: '8px' }}>
        <Divider orientation="left" style={{ margin: "0 0 12px 0", fontSize: '12px', color: '#8c8c8c' }}>
          <Space size={6}><GiftOutlined style={{ color: "#faad14" }} />{t("Other Assets")}</Space>
        </Divider>
        <Card
          style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #f0f0f0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
          }}
          bodyStyle={{ padding: '16px' }}
        >
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            <div
              style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
              onClick={() => navigate("/web-tokens")}
            >
              <Space size={8}>
                <img src="./assets/LogoW.png" alt="Logo" width={16} />
                <Text>$WEB</Text>
              </Space>
              <Text strong>
                {isLoading ? (
                  <Spin size="small" />
                ) : (
                  <>
                    {userData?.web_tokens ?? 0}
                    {!priceLoading && currentPrice > 0 && (
                      <Text type="secondary" style={{ fontSize: "11px", marginLeft: 4 }}>
                        (≈${calculateWebValue()})
                      </Text>
                    )}
                  </>
                )}
              </Text>
            </div>
            <div
              style={{ display: "flex", justifyContent: "space-between", cursor: "pointer" }}
              onClick={() => navigate("/loan-issuance", { state: { useBonus: true } })}
            >
              <Space size={8}>
                <GiftOutlined style={{ color: "#1890ff" }} />
                <Text>{t("bonus balance")}</Text>
              </Space>
              <Text strong>{isLoading ? <Spin size="small" /> : `$${userData?.bonus_balance ?? 0}`}</Text>
            </div>
          </Space>
        </Card>
      </div>

      {/* UNFREEZE BANNER - ALWAYS AT THE BOTTOM */}
      {unfreezeEligible && (
        <Card
          size="small"
          onClick={() => navigate("/unfreeze")}
          style={{
            background: "#e6f4ff",
            borderRadius: "12px",
            cursor: "pointer",
            border: "1px solid #91caff",
            marginTop: '8px'
          }}
          className="hover-card"
        >
          <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
            <Space size={10}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 8,
                  background: "#1677ff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LockOutlined style={{ fontSize: "18px", color: "#fff" }} />
              </div>
              <Space direction="vertical" size={0}>
                <Text strong style={{ fontSize: "13px" }}>
                  {t("unfreeze.title")}
                </Text>
                <Text type="secondary" style={{ fontSize: "11px" }}>
                  {t("unfreeze.banner_subtitle")}
                </Text>
              </Space>
            </Space>
            <RightOutlined style={{ color: "#1677ff" }} />
          </Space>
        </Card>
      )}

      {/* DEPOSIT DRAWER */}
      <Drawer
        title={isVoucherDepositMode ? `🎫 ${t("Doubling Voucher — Top up")}` : t("Top up working balance")}
        placement="bottom"
        onClose={() => {
          if (!depositSending) {
            setIsDepositDrawerOpen(false);
            setIsVoucherDepositMode(false);
            if (onVoucherDrawerClose) onVoucherDrawerClose();
          }
        }}
        open={isDepositDrawerOpen}
        height="auto"
        // В режиме ваучера контента больше, чем помещается на экран телефона.
        // При height="auto" drawer растёт вверх за пределы вьюпорта и шапка
        // обрезается, поэтому ограничиваем тело и включаем прокрутку.
        bodyStyle={{
          padding: '24px 16px 32px 16px',
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
          maxHeight: '70vh',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
        headerStyle={{ borderBottom: '1px solid #f0f0f0', flex: '0 0 auto' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          {isVoucherDepositMode ? (
            <Alert
              type="success"
              showIcon
              icon={<ThunderboltOutlined style={{ color: "#52c41a" }} />}
              message={`💎 ${t("Personal Multi-Voucher Doubling (2x)")}`}
              description={t("When you top up, the amount is automatically doubled and credited to your working balance with a 10% monthly payout.")}
              style={{ borderRadius: "12px" }}
            />
          ) : (
            <Text type="secondary" style={{ fontSize: 13 }}>
              {t("Funds will be transferred from your connected wallet to your working balance.")}
            </Text>
          )}

          <div>
            <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
              {t("Currency")}
            </Text>
            <Select
              value={depositCurrency}
              onChange={(v) => {
                setDepositCurrency(v as "TON" | "USDT");
                setDepositAmount(v === "USDT" ? 100 : 50);
              }}
              style={{ width: "100%" }}
              size="large"
              disabled={depositSending}
            >
              <Select.Option value="USDT">
                <Space>
                  <img src="./assets/UsdtLogo.webp" alt="USDT" width={18} />
                  USDT
                </Space>
              </Select.Option>
              <Select.Option value="TON">
                <Space>
                  <img src="./assets/gramIcon.png" alt="GRAM" width={18} />
                  GRAM
                </Space>
              </Select.Option>
            </Select>
          </div>

          <div>
            <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
              {t("Amount")} ({curLabel})
            </Text>
            <InputNumber
              value={depositAmount}
              onChange={(v) => setDepositAmount(v as number | null)}
              min={minDeposit}
              step={depositCurrency === "USDT" ? 1 : 0.1}
              size="large"
              style={{ width: "100%" }}
              placeholder={`${t("Minimum")} ${minDeposit} ${curLabel}`}
              disabled={depositSending}
            />
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
              {t("Minimum")}: {minDeposit} {curLabel}
            </Text>
            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
              {[
                depositCurrency === "USDT" ? 50 : 20,
                depositCurrency === "USDT" ? 100 : 50,
                depositCurrency === "USDT" ? 300 : 150,
              ].map((preset) => (
                <Button
                  key={preset}
                  size="small"
                  onClick={() => setDepositAmount(preset)}
                  disabled={depositSending}
                  style={{ borderRadius: "8px" }}
                >
                  {preset} {curLabel}
                </Button>
              ))}
            </div>
          </div>

          {isVoucherDepositMode && (
            <Card
              size="small"
              style={{
                background: "#f6ffed",
                borderRadius: "12px",
                border: "1px solid #b7eb8f",
              }}
            >
              <Space direction="vertical" style={{ width: "100%" }} size={6}>
                <Text strong style={{ color: "#389e0d", fontSize: "13px" }}>
                  📊 {t("Voucher summary")}:
                </Text>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                  <Text>{t("Deposit:")}</Text>
                  <Text strong>{voucherDeposit} {curLabel}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px" }}>
                  <Text style={{ color: "#52c41a" }}>+ {t("Voucher bonus (2x):")}</Text>
                  <Text strong style={{ color: "#52c41a" }}>+{voucherBonus} {curLabel}</Text>
                </div>
                <Divider style={{ margin: "4px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "14px" }}>
                  <Text strong>{t("Will be credited:")}</Text>
                  <Text strong style={{ color: "#1890ff", fontSize: "15px" }}>
                    {(voucherDeposit + voucherBonus).toFixed(depositCurrency === "USDT" ? 2 : 4)} {curLabel}
                  </Text>
                </div>
                {voucherBonus < voucherDeposit && (
                  <Text type="warning" style={{ fontSize: 11 }}>
                    {t("Bonus is capped at {{max}} {{currency}} per use.", { max: voucherMaxBonus, currency: curLabel })}
                  </Text>
                )}
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {t("The bonus is credited automatically after the deposit is confirmed on-chain.")}
                </Text>
              </Space>
            </Card>
          )}

          <Button
            type="primary"
            block
            size="large"
            loading={depositSending}
            onClick={handleDeposit}
            disabled={!depositAmount || depositAmount < minDeposit}
            style={{ borderRadius: '12px', height: '48px', fontWeight: 600, background: '#52c41a', borderColor: '#52c41a' }}
          >
            {isVoucherDepositMode
              ? t("Top up {{amount}} {{currency}} and Double", { amount: depositAmount || minDeposit, currency: curLabel })
              : `${t("Top up")} ${depositAmount ? `${depositAmount} ${curLabel}` : ""}`}
          </Button>

        </Space>
      </Drawer>

      {/* ISSUE LOAN DRAWER */}
      <Drawer
        title={t("Issue Loans")}
        placement="bottom"
        onClose={() => setIsIssueLoanDrawerOpen(false)}
        open={isIssueLoanDrawerOpen}
        height="auto"
        bodyStyle={{ padding: '24px 16px 40px 16px', borderTopLeftRadius: '20px', borderTopRightRadius: '20px' }}
        headerStyle={{ borderBottom: '1px solid #f0f0f0' }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Button
            block
            size="large"
            onClick={() => { navigate("/loan-overnight"); setIsIssueLoanDrawerOpen(false); }}
            style={{ height: 72, borderRadius: 16, border: '1px solid #f0f0f0', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#e6f4ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 16 }}>
              <RocketOutlined style={{ fontSize: 22, color: '#1677ff' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <Text strong style={{ fontSize: '15px', lineHeight: 1.2 }}>{t("Overnight loans")}</Text>
              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>{t("Short term (1 day), daily profit")}</Text>
            </div>
          </Button>

          <Button
            block
            size="large"
            onClick={() => { navigate("/loan-issuance"); setIsIssueLoanDrawerOpen(false); }}
            style={{ height: 72, borderRadius: 16, border: '1px solid #f0f0f0', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 12, background: '#f6ffed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 16 }}>
              <BankOutlined style={{ fontSize: 22, color: '#52c41a' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}>
              <Text strong style={{ fontSize: '15px', lineHeight: 1.2 }}>{t("Long-term loans")}</Text>
              <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.2 }}>{t("Higher returns, variable terms")}</Text>
            </div>
          </Button>
        </Space>
      </Drawer>
    </Space>
  );
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  .hover-card:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  }
`;
document.head.appendChild(styleSheet);

export default Balances;
